import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useDashboardData, getChartData } from '../../hooks/useDashboardData';
import { useResponsive } from '../../hooks/useResponsive';
import { 
  useDiningBehaviorMonthlyTrendQuery, 
  useBehaviorOptionsMonthlyTrendQuery
} from '../../hooks/queries/useDashboardQuery';
import './DiningBehaviorChart.css';
import '../charts/ChartStyles.css';


interface DiningBehavior {
  behavior: string;
  net_sales: string;
  order_count: string;
  percentage: string;
}

interface DiningOption {
  cleaned_dining_option: string;
  net_sales: string;
  order_count: string;
  percentage: string;
  recalculated_percentage?: number;  
  behavior?: string; 
}

interface MonthlyBehaviorData {
  year_month: string;
  behaviors: {
    [key: string]: {
      behavior: string;
      gross_sales: string;
      total_refunds: string;
      net_sales: string;
      order_count: number;
    }
  }
}

interface MonthlyBehaviorOptionsData {
  year_month: string;
  options: {
    [key: string]: {
      cleaned_dining_option: string;
      gross_sales: string;
      total_refunds: string;
      net_sales: string;
      order_count: number;
    }
  }
}

interface MonthlyBehaviorOptionsResponse {
  activeOptions: string[];
  monthlyData: MonthlyBehaviorOptionsData[];
}

interface ModalPosition {
  y: number;
}

interface MainBehaviorChartProps {
  dashboardData: any;
  selectedBehavior: string | null;
  onBehaviorSelect: (behavior: string | null) => void;
  onMonthlyTrendClick: (event: React.MouseEvent) => void;
  isFiltersApplying: boolean;
  filters: {
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
    diningGroup?: string[];
    menuGroup?: string[];
  };
}

interface MonthlyTrendModalProps {
  show: boolean;
  onClose: () => void;
  salesforceId: string | null;
  modalPosition: ModalPosition;
  
}


const extractNumber = (value: string): number => {
  if (typeof value === 'string') {
    return parseFloat(value.replace(/[^0-9.-]+/g, ''));
  }
  return value || 0;
};

const extractPercentage = (value: string): number => {
  if (typeof value === 'string') {
    return parseFloat(value.replace('%', ''));
  }
  return value || 0;
};

const formatMonthForDisplay = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-');
  return `${year}/${month}`;
};

const calculateYAxisRange = (values: number[]) => {
  const validValues = values.filter(v => v > 0 && isFinite(v));
  
  if (validValues.length === 0) {
    return { min: 0, max: 100 };
  }
  
  const minValue = Math.min(...validValues);
  const maxValue = Math.max(...validValues);
  const range = maxValue - minValue;
  
  return {
    min: Math.max(0, Math.floor(minValue - range * 0.05)),
    max: Math.ceil(maxValue + range * 0.05)
  };
};


const MainBehaviorChart = React.memo<MainBehaviorChartProps>(({ 
  dashboardData, 
  selectedBehavior, 
  onBehaviorSelect,
  onMonthlyTrendClick,
  isFiltersApplying,
  filters
}) => {
  const { t } = useTranslation();
  
  const colorPalette = {
    dineInColor: '#F97F76',
    takeOutColor: '#474646',
    deliveryColor: '#F9C27B',
    emphasisColor: '#F52314'
  };

  
  const isLaptop = window.innerWidth <= 1600;
  const isIPad = window.innerWidth <= 1024;
  const isIPhone = window.innerWidth <= 480;

  
  const { chartData, diningOptionsData } = useMemo(() => {
    if (!dashboardData) return { chartData: null, diningOptionsData: null };

    
    const chartData = dashboardData?.chartData?.diningBehavior || [];
    const optionsData = dashboardData?.chartData?.diningBehaviorOptions || [];
    
    
    const optionsByBehavior: { [key: string]: any[] } = {};
    
    if (optionsData && optionsData.length > 0) {
      optionsData.forEach((item: any) => {
        const behavior = item.dining_behavior;
        if (!optionsByBehavior[behavior]) {
          optionsByBehavior[behavior] = [];
        }
        optionsByBehavior[behavior].push(item);
      });
    }
    
    const diningOptionsData = {
      optionsByBehavior: optionsByBehavior,
      allOptions: optionsData || []
    };

    return { chartData, diningOptionsData };
  }, [dashboardData, filters]);

  
  const recalculatedData = useMemo(() => {
    if (!diningOptionsData || !diningOptionsData.optionsByBehavior) return [];

    const { optionsByBehavior, allOptions } = diningOptionsData;
    
    
    let barData = selectedBehavior && optionsByBehavior[selectedBehavior] 
      ? optionsByBehavior[selectedBehavior]
      : allOptions;
    
    
    barData = [...barData].sort((a: any, b: any) => 
      extractNumber(b.net_sales) - extractNumber(a.net_sales)
    );

    
    const targetOptions = selectedBehavior && optionsByBehavior[selectedBehavior] 
      ? optionsByBehavior[selectedBehavior]
      : allOptions;
    
    const totalSales = targetOptions.reduce((sum: number, option: any) => 
      sum + extractNumber(option.net_sales), 0
    );
    
    
    return barData.map((option: any) => {
      const optionSales = extractNumber(option.net_sales);
      const recalculatedPercentage = totalSales > 0 ? (optionSales / totalSales) * 100 : 0;
      return { ...option, recalculated_percentage: recalculatedPercentage };
    });
  }, [diningOptionsData, selectedBehavior]);

  
  const chartOption = useMemo(() => {
    if (!chartData || chartData.length === 0) return {};
    
    const pieData = chartData.map((behavior: any) => ({
      name: behavior.dining_behavior,
      value: extractNumber(behavior.net_sales),
      percentage: extractPercentage(behavior.percentage),
      orderCount: behavior.order_count
    }));
    
    const categories = recalculatedData.map((item: any) => item.cleaned_dining_option);
    const values = recalculatedData.map((item: any) => extractNumber(item.net_sales));
    const percentages = recalculatedData.map((item: any) => item.recalculated_percentage || 0);
    const orderCounts = recalculatedData.map((item: any) => item.order_count);
    
    const getBehaviorColor = (index: number): string => {
      const behaviorType = recalculatedData[index]?.dining_behavior;
      
      if (behaviorType === 'DINE_IN') {
        return colorPalette.dineInColor;
      } else if (behaviorType === 'TAKE_OUT') {
        return colorPalette.takeOutColor;
      } else if (behaviorType === 'DELIVERY') {
        return colorPalette.deliveryColor;
      }
      return '#ccc';
    };
    
    const getBehaviorType = (index: number): string => {
      return recalculatedData[index]?.dining_behavior || '';
    };

    return {
      grid: [
        {
          left: '2%',
          right: '58%',
          top: isLaptop ? '6%' : '8%',
          bottom: isLaptop ? '6%' : '8%',
          containLabel: false
        },
        {
          left: '48%',
          right: '8%',
          top: isIPhone ? '0%' : (isLaptop ? '6%' : '8%'),
          bottom: isIPhone ? '0%' : (isLaptop ? '6%' : '8%'),
          containLabel: true,
          show: false
        }
      ],
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          if (params.seriesType === 'pie') {
            let borderColor: string;
            
            if (params.name === 'DINE_IN') {
              borderColor = colorPalette.dineInColor;
            } else if (params.name === 'TAKE_OUT') {
              borderColor = colorPalette.takeOutColor;
            } else {
              borderColor = colorPalette.deliveryColor;
            }
            
            const dataItem = pieData.find((item: any) => item.name === params.name);
            
            return `<div style="padding: 3px; border-left: 4px solid ${borderColor};">
              <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${params.name}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('behaviorChart.sales')}:</span>
                <span style="font-weight: bold;">$${params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('behaviorChart.orders')}:</span>
                <span style="font-weight: bold;">${dataItem?.orderCount || 0}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="margin-right: 15px;">${t('behaviorChart.percentage')}:</span>
                <span style="font-weight: bold;">${dataItem?.percentage.toFixed(2) || 0}%</span>
              </div>
            </div>`;
          }
          
          if (params.seriesType === 'bar') {
            const idx = params.dataIndex;
            const borderColor = getBehaviorColor(idx);
            const behaviorType = getBehaviorType(idx);
            
            return `<div style="padding: 3px; border-left: 4px solid ${borderColor};">
              <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${categories[idx]}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('behaviorChart.type')}:</span>
                <span style="font-weight: bold;">${behaviorType}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('behaviorChart.sales')}:</span>
                <span style="font-weight: bold;">$${values[idx].toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('behaviorChart.orders')}:</span>
                <span style="font-weight: bold;">${orderCounts[idx]}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="margin-right: 15px;">${t('behaviorChart.percentage')}:</span>
                <span style="font-weight: bold;">${percentages[idx].toFixed(2)}%</span>
              </div>
            </div>`;
          }
          
          return '';
        }
      },
      series: [
        {
          name: '다이닝 행동',
          type: 'pie',
          radius: isIPhone ? '45%' : (isIPad ? '55%' : (isLaptop ? '55%' : '70%')),
          center: ['23%', '50%'],
          avoidLabelOverlap: true,
          selectedMode: 'single',
          itemStyle: {
            borderWidth: isLaptop ? 1.5 : 2,
            borderColor: '#fff'
          },
          label: {
            show: true,
            position: 'inside',
            formatter: function(params: any) {
              const item = pieData.find((d: any) => d.name === params.name);
              if (item) {
                return `${params.name}\n${item.percentage.toFixed(1)}%`;
              }
              return params.name;
            },
            fontSize: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 12 : 13)),
            fontWeight: 'bold'
          },
          labelLine: {
            show: false
          },
          data: (() => {
            const validBehaviors: any[] = [];
            
            const dineInValue = pieData.find((item: any) => item.name === 'DINE_IN')?.value || 0;
            if (dineInValue > 0) {
              validBehaviors.push({
                name: 'DINE_IN',
                value: dineInValue,
                itemStyle: {
                  color: colorPalette.dineInColor,
                  opacity: selectedBehavior && selectedBehavior !== 'DINE_IN' ? 0.5 : 1
                },
                label: {
                  color: '#000'
                }
              });
            }
            
            const takeOutValue = pieData.find((item: any) => item.name === 'TAKE_OUT')?.value || 0;
            if (takeOutValue > 0) {
              validBehaviors.push({
                name: 'TAKE_OUT',
                value: takeOutValue,
                itemStyle: {
                  color: colorPalette.takeOutColor,
                  opacity: selectedBehavior && selectedBehavior !== 'TAKE_OUT' ? 0.5 : 1
                },
                label: {
                  color: '#fff'
                }
              });
            }
            
            const deliveryValue = pieData.find((item: any) => item.name === 'DELIVERY')?.value || 0;
            if (deliveryValue > 0) {
              validBehaviors.push({
                name: 'DELIVERY',
                value: deliveryValue,
                itemStyle: {
                  color: colorPalette.deliveryColor,
                  opacity: selectedBehavior && selectedBehavior !== 'DELIVERY' ? 0.5 : 1
                },
                label: {
                  color: '#000'
                }
              });
            }
            
            return validBehaviors;
          })()
        },
        {
          name: '다이닝 옵션',
          type: 'bar',
          barWidth: isIPhone ? '50%' : (isLaptop ? '60%' : '70%'),
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: values.map((value: number, index: number) => ({
            value: value,
            itemStyle: {
              color: getBehaviorColor(index)
            }
          })),
          label: {
            show: true,
            position: 'right',
            formatter: function(params: any) {
              const idx = params.dataIndex;
              const value = params.value;
              const maxValue = Math.max(...values);
              const formattedValue = `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
              const percentage = percentages[idx].toFixed(1);
              
              
              const shouldBreakLine = (value / maxValue) > 0.6;    
              
              if (shouldBreakLine) {
                return `${formattedValue}\n(${percentage}%)`;
              } else {
                return `${formattedValue} (${percentage}%)`;
              }
            },
            fontWeight: 'bold',
            fontSize: isIPhone ? 9 : (isIPad ? 10 : (isLaptop ? 10 : 11)),
            color: '#333'
          }
        }
      ],
      xAxis: [
        { type: 'value', gridIndex: 0, show: false },
        { 
          type: 'value', 
          gridIndex: 1,
          show: false
        }
      ],
      yAxis: [
        { type: 'category', gridIndex: 0, show: false },
        { 
          type: 'category', 
          gridIndex: 1, 
          data: categories,
          axisLabel: {
            interval: 0,
            formatter: function(value: string) {
              if (value.length > 10) {
                const words = value.split(' ');
                if (words.length > 1) {
                  const mid = Math.ceil(words.length / 2);
                  return words.slice(0, mid).join(' ') + '\n' + words.slice(mid).join(' ');
                } else {
                  return value.substring(0, 10) + '\n' + value.substring(10);
                }
              }
              return value;
            },
            fontWeight: 'bold',
            fontSize: isIPhone ? 9 : (isIPad ? 10 : (isLaptop ? 10 : 12)),
            color: '#333',
            lineHeight: isLaptop ? 10 : 12
          }
        }
      ]
    };
  }, [chartData, recalculatedData, selectedBehavior, diningOptionsData, t, isLaptop]);

  
  const handleChartEvents = useMemo(() => ({
    'click': function(params: any) {
      if (params.seriesType === 'pie') {
        if (params.name === selectedBehavior) {
          onBehaviorSelect(null);
        } else {
          onBehaviorSelect(params.name);
        }
      }
    }
  }), [selectedBehavior, onBehaviorSelect]);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="behavior-chart-container">
        <div className="behavior-error">
          <div className="behavior-error-text">No chart data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="behavior-chart-container">
      <div className="behavior-navigation">
        <button 
          className="behavior-monthly-trend-button" 
          onClick={onMonthlyTrendClick}
          disabled={isFiltersApplying}
          style={{
            fontSize: isLaptop ? '11px' : '12px',
            padding: isLaptop ? '5px 5px' : '6px 12px'
          }}
        >
          {t('behaviorChart.viewMonthlyTrend')}
        </button>
      </div>
      {selectedBehavior && (
        <div className="behavior-filter-info">
          <button 
            className="behavior-reset-button" 
            onClick={() => onBehaviorSelect(null)}
            style={{
              fontSize: isLaptop ? '11px' : '13px',
              padding: isLaptop ? '4px 12px' : '5px 15px'
            }}
          >
            {t('behaviorChart.viewAll')}
          </button>
        </div>
      )}
      <ReactECharts 
        option={chartOption} 
        style={{ height: isIPhone ? '320px' : (isIPad ? '420px' : (isLaptop ? '380px' : '460px')), width: '100%' }}
        className="behavior-echarts"
        opts={{ renderer: 'canvas' }}
        onEvents={handleChartEvents}
      />
    </div>
  );
});


const MonthlyTrendModal = React.memo<MonthlyTrendModalProps>(({ 
  show,
  onClose,
  salesforceId,
  modalPosition 
}) => {
  const { t } = useTranslation();
  const { isFiltersApplying } = useFilters();
  const { isiPhone } = useResponsive();
  const isPhoneScreen = window.innerWidth <= 480;

  
  const [trendView, setTrendView] = useState<'behaviors' | 'options'>('behaviors');
  const [selectedTrendBehavior, setSelectedTrendBehavior] = useState<string | null>(null);

  
  const { 
    data: monthlyTrendData, 
    isLoading: trendLoading, 
    error: trendError 
  } = useDiningBehaviorMonthlyTrendQuery(salesforceId || '', show && !!salesforceId);

  const { 
    data: optionsMonthlyData, 
    isLoading: optionsLoading 
  } = useBehaviorOptionsMonthlyTrendQuery(
    salesforceId || '', 
    selectedTrendBehavior || '', 
    show && !!salesforceId && !!selectedTrendBehavior && trendView === 'options'
  );
  
  const behaviorChartRef = useRef<any>(null);
  
  const colorPalette = {
    dineInColor: '#F97F76',
    takeOutColor: '#474646',
    deliveryColor: '#F9C27B',
    emphasisColor: '#F52314'
  };

  const optionColors = [
    '#F97F76', '#474646', '#F9C27B', '#4285F4', '#34A853', '#EA4335',
    '#9C27B0', '#FF9800', '#795548', '#607D8B', '#E91E63', '#00BCD4',
    '#8BC34A', '#FFC107', '#673AB7', '#009688'
  ];

  
  const isLoading = trendLoading || optionsLoading;

  
  const displayError = trendError ? 
    (trendError as any)?.message || t('behaviorChart.monthlyTrendError') : 
    null;

  const updateBehaviorLineStyles = (selected: {[key: string]: boolean}) => {
    if (!behaviorChartRef.current) return;
    
    const echartInstance = behaviorChartRef.current.getEchartsInstance();
    const currentOption = echartInstance.getOption();
    
    const activeBehaviors = Object.keys(selected).filter(key => selected[key]);
    const totalActiveCount = activeBehaviors.length;
    
    const newSeries = currentOption.series.map((series: any, index: number) => {
      const isActive = selected[series.name];
      
      if (!isActive) return series;
      
      if (totalActiveCount === 1) {
        return {
          ...series,
          lineStyle: {
            ...series.lineStyle,
            width: 2,
            type: 'solid'
          }
        };
      }
      
      const activeSeriesNames = currentOption.series
        .filter((s: any) => selected[s.name])
        .map((s: any) => s.name);
      
      const isLastActiveSeries = series.name === activeSeriesNames[activeSeriesNames.length - 1];
      
      return {
        ...series,
        lineStyle: {
          ...series.lineStyle,
          width: isLastActiveSeries ? 4 : 2,
          type: isLastActiveSeries ? 'solid' : 'dashed'
        }
      };
    });
    
    echartInstance.setOption({
      series: newSeries
    });
  };

  const getBehaviorChartEvents = () => {
    return {
      'legendselectchanged': function(params: any) {
        updateBehaviorLineStyles(params.selected);
      }
    };
  };

  const getBehaviorTrendOption = () => {
    if (!monthlyTrendData || monthlyTrendData.length === 0) {
      return {};
    }

    const months = monthlyTrendData.map((item: MonthlyBehaviorData) => formatMonthForDisplay(item.year_month));
    const allValues: number[] = [];

    const dineInData = monthlyTrendData.map((monthData: MonthlyBehaviorData) => {
      const value = monthData.behaviors['DINE_IN'] ? extractNumber(monthData.behaviors['DINE_IN'].net_sales) : 0;
      if (value > 0) allValues.push(value);
      return value;
    });

    const takeOutData = monthlyTrendData.map((monthData: MonthlyBehaviorData) => {
      const value = monthData.behaviors['TAKE_OUT'] ? extractNumber(monthData.behaviors['TAKE_OUT'].net_sales) : 0;
      if (value > 0) allValues.push(value);
      return value;
    });

    const deliveryData = monthlyTrendData.map((monthData: MonthlyBehaviorData) => {
      const value = monthData.behaviors['DELIVERY'] ? extractNumber(monthData.behaviors['DELIVERY'].net_sales) : 0;
      if (value > 0) allValues.push(value);
      return value;
    });

    const totalValues = dineInData.map((dineIn, index) => dineIn + takeOutData[index] + deliveryData[index]);
    allValues.push(...totalValues);

    const validBehaviors: string[] = [];
    const validData: number[][] = [];
    const validColors: Array<{
      name: string;
      color: string;
      rgba: string;
      rgbaLight: string;
    }> = [];

    const totalDineIn = dineInData.reduce((sum, val) => sum + val, 0);
    const totalTakeOut = takeOutData.reduce((sum, val) => sum + val, 0);
    const totalDelivery = deliveryData.reduce((sum, val) => sum + val, 0);

    if (totalDineIn > 0) {
      validBehaviors.push('DINE_IN');
      validData.push(dineInData);
      validColors.push({
        name: 'DINE_IN',
        color: colorPalette.dineInColor,
        rgba: 'rgba(249, 127, 118, 0.6)',
        rgbaLight: 'rgba(249, 127, 118, 0.6)'
      });
    }

    if (totalTakeOut > 0) {
      validBehaviors.push('TAKE_OUT');
      validData.push(takeOutData);
      validColors.push({
        name: 'TAKE_OUT',
        color: colorPalette.takeOutColor,
        rgba: 'rgba(71, 70, 70, 0.6)',
        rgbaLight: 'rgba(71, 70, 70, 0.6)'
      });
    }

    if (totalDelivery > 0) {
      validBehaviors.push('DELIVERY');
      validData.push(deliveryData);
      validColors.push({
        name: 'DELIVERY',
        color: colorPalette.deliveryColor,
        rgba: 'rgba(249, 196, 123, 0.6)',
        rgbaLight: 'rgba(249, 196, 123, 0.6)'
      });
    }

    const yAxisRange = calculateYAxisRange(allValues);

    return {
      grid: {
        left: '4%',
        right: '4%',
        top: (window.innerWidth <= 480) ? '35%' : '25%',
        bottom: (window.innerWidth <= 480) ? '0%' : '2%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: 11
        },
        formatter: function(params: any) {
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: 6px; font-size: 12px;">${monthStr}</div>`;
          
          const totalValue = params.reduce((sum: number, param: any) => sum + param.value, 0);
          
          html += `<div style="display: flex; justify-content: space-between; margin-bottom: 6px; padding-bottom: 3px; border-bottom: 1px solid #eee;">
            <span style="font-weight: bold; color: #474646;">${t('behaviorChart.total')}:</span>
            <span style="font-weight: bold; font-size: 12px; color: #474646;">$${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>`;
          
          params.forEach((param: any) => {
            const percentage = totalValue > 0 ? ((param.value / totalValue) * 100).toFixed(1) : '0.0';
            html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 8px; height: 8px; background-color: ${param.color}; margin-right: 4px; border-radius: 50%;"></span>
                <strong>${param.seriesName}:</strong>
              </span>
              <span style="font-weight: bold; font-size: 11px;">
                $${param.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                <span style="color: #999; font-size: 10px;">(${percentage}%)</span>
              </span>
            </div>`;
          });
          
          html += '</div>';
          return html;
        }
      },
      legend: {
        data: validBehaviors,
        top: '6%',
        textStyle: {
          fontSize: 12,
          fontWeight: 'bold'
        },
        itemGap: 15,
        selector: false,
        itemStyle: {
          borderWidth: 0
        }
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '1%',
          style: {
            text: t('behaviorChart.chartHintStacked'),
            fontSize: 10,
            fontWeight: 'normal',
            fill: '#666',
            fontStyle: 'italic'
          }
        }
      ],
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: 10,
          fontWeight: 'bold',
          rotate: months.length > 8 ? 45 : 0,
          margin: 6
        },
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value',
        name: t('behaviorChart.yAxisMonthlySales'),
        min: yAxisRange.min,
        scale: true,
        nameTextStyle: {
          fontSize: 11,
          fontWeight: 'bold'
        },
        axisLabel: {
          fontSize: 10,
          formatter: '${value}'
        },
        splitLine: {
          lineStyle: {
            opacity: 0.3
          }
        }
      },
      series: validColors.map((colorInfo, index) => ({
        name: colorInfo.name,
        type: 'line',
        stack: 'total',
        data: validData[index],
        lineStyle: {
          width: index === validColors.length - 1 ? 4 : 2,
          color: colorInfo.color,
          type: index === validColors.length - 1 ? 'solid' : 'dashed'
        },
        itemStyle: {
          color: colorInfo.color
        },
        areaStyle: {
          color: colorInfo.rgba
        },
        symbol: 'circle',
        symbolSize: 6
      }))
    };
  };

  const getOptionsTrendOption = () => {
    if (!optionsMonthlyData || optionsMonthlyData.monthlyData.length === 0) {
      return {};
    }

    const months = optionsMonthlyData.monthlyData.map((item: MonthlyBehaviorOptionsData) => formatMonthForDisplay(item.year_month));
    const allValues: number[] = [];

    const series = optionsMonthlyData.activeOptions.map((option: string, index: number) => {
      const optionData = optionsMonthlyData.monthlyData.map((monthData: MonthlyBehaviorOptionsData) => {
        const optionInfo = monthData.options[option];
        const value = optionInfo ? extractNumber(optionInfo.net_sales) : 0;
        if (value > 0) allValues.push(value);
        return value;
      });

      return {
        name: option,
        type: 'line',
        data: optionData,
        lineStyle: {
          width: 2.5,
          color: optionColors[index % optionColors.length]
        },
        itemStyle: {
          color: optionColors[index % optionColors.length]
        },
        symbol: 'circle',
        symbolSize: 5,
        smooth: false
      };
    });

    const yAxisRange = calculateYAxisRange(allValues);

    return {
      grid: {
        left: '4%',
        right: '4%',
        top: (window.innerWidth <= 480) ? '35%' : '25%',
        bottom: (window.innerWidth <= 480) ? '0%' : '2%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: 11
        },
        formatter: function(params: any) {
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: 6px; font-size: 12px;">${monthStr}</div>`;
          
          const validParams = params.filter((param: any) => param.value > 0);
          
          validParams.forEach((param: any) => {
            html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 8px; height: 8px; background-color: ${param.color}; margin-right: 4px; border-radius: 50%;"></span>
                <strong>${param.seriesName}:</strong>
              </span>
              <span style="font-weight: bold; font-size: 11px;">$${param.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>`;
          });
          
          html += '</div>';
          return html;
        }
      },
      legend: {
        data: optionsMonthlyData.activeOptions,
        top: '6%',
        left: 'center',
        width: '90%',
        textStyle: {
          fontSize: 11,
          fontWeight: 'bold'
        },
        itemGap: 8,
        itemWidth: 12,
        itemHeight: 8,
        orient: 'horizontal',
        selector: false
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '1%',
          style: {
            text: t('behaviorChart.chartHintOptions'),
            fontSize: 10,
            fontWeight: 'normal',
            fill: '#666',
            fontStyle: 'italic'
          }
        }
      ],
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: 10,
          fontWeight: 'bold',
          rotate: months.length > 8 ? 45 : 0,
          margin: 6
        },
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value',
        name: t('behaviorChart.yAxisMonthlySales'),
        min: yAxisRange.min,
        scale: true,
        nameTextStyle: {
          fontSize: 11,
          fontWeight: 'bold'
        },
        axisLabel: {
          fontSize: 10,
          formatter: '${value}'
        },
        splitLine: {
          lineStyle: {
            opacity: 0.3
          }
        }
      },
      series: series
    };
  };

  if (!show) return null;

  return (
    <div className="behavior-trend-overlay" onClick={onClose}>
      <div 
        className="behavior-trend-modal" 
        onClick={(e) => e.stopPropagation()}
        style={isPhoneScreen ? {
          position: 'fixed',
          left: '2.5%',
          top: `${modalPosition.y}px`,
          width: '95%',
          height: '500px',
          margin: '0',
          transform: 'none'
        } : {
          position: 'fixed',
          left: '50%',
          top: `${modalPosition.y}px`,
          transform: 'translateX(-50%)'
        }}
      >
        <div className="behavior-trend-modal-header">
          <h3 className="behavior-trend-modal-title">
            {trendView === 'behaviors' 
              ? t('behaviorChart.modalBehaviorTitle')
              : t('behaviorChart.modalOptionsTitle', { behavior: selectedTrendBehavior })}
          </h3>
          <div className="behavior-trend-modal-subtitle">
            {trendView === 'behaviors' 
              ? t('behaviorChart.modalBehaviorSubtitle')
              : t('behaviorChart.modalOptionsSubtitle')}
          </div>
          
          {trendView === 'behaviors' && (
            <div className="behavior-trend-modal-nav">
              <div className="behavior-option-buttons">
                <button 
                  className="behavior-option-button dine-in"
                  onClick={() => {
                    setSelectedTrendBehavior('DINE_IN');
                    setTrendView('options');
                  }}
                  disabled={isLoading || isFiltersApplying}
                >
                  {t('behaviorChart.viewDineInOptions')}
                </button>
                <button 
                  className="behavior-option-button take-out"
                  onClick={() => {
                    setSelectedTrendBehavior('TAKE_OUT');
                    setTrendView('options');
                  }}
                  disabled={isLoading || isFiltersApplying}
                >
                  {t('behaviorChart.viewTakeOutOptions')}
                </button>
                <button 
                  className="behavior-option-button delivery"
                  onClick={() => {
                    setSelectedTrendBehavior('DELIVERY');
                    setTrendView('options');
                  }}
                  disabled={isLoading || isFiltersApplying}
                >
                  {t('behaviorChart.viewDeliveryOptions')}
                </button>
              </div>
            </div>
          )}
          
          {trendView === 'options' && (
            <div className="behavior-trend-modal-nav">
              <button 
                className="behavior-trend-back-button" 
                onClick={() => {
                  setTrendView('behaviors');
                  setSelectedTrendBehavior(null);
                }}
              >
                {t('behaviorChart.backToBehaviors')}
              </button>
            </div>
          )}
          
          <button className="behavior-trend-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="behavior-trend-modal-content">
          {isLoading ? (
            <div className="behavior-trend-loading">
              <div className="behavior-trend-loader"></div>
              <div>{t('behaviorChart.loadingMonthlyTrend')}</div>
            </div>
          ) : displayError ? (
            <div className="behavior-trend-error">
              <div className="behavior-trend-error-icon">⚠️</div>
              <div>{displayError}</div>
            </div>
          ) : (() => {
            
            if (trendView === 'behaviors') {
              if (monthlyTrendData && monthlyTrendData.length > 0) {
                return (
                  <ReactECharts 
                    ref={behaviorChartRef}
                    option={getBehaviorTrendOption()} 
                    style={{height: '100%', width: '100%'}}
                    className="behavior-trend-chart"
                    opts={{renderer: 'canvas'}}
                    onEvents={getBehaviorChartEvents()}
                    key="behavior-trend-chart"
                  />
                );
              } else {
                return (
                  <div className="behavior-trend-empty">
                    <div>{t('behaviorChart.noBehaviorTrendData')}</div>
                  </div>
                );
              }
            } else if (trendView === 'options') {
              if (optionsMonthlyData && optionsMonthlyData.monthlyData && optionsMonthlyData.monthlyData.length > 0) {
                return (
                  <ReactECharts 
                    option={getOptionsTrendOption()} 
                    style={{height: '100%', width: '100%'}}
                    className="behavior-trend-chart"
                    opts={{renderer: 'canvas'}}
                    key="options-trend-chart"
                  />
                );
              } else {
                return (
                  <div className="behavior-trend-empty">
                    <div>{t('behaviorChart.noOptionsTrendData')}</div>
                  </div>
                );
              }
            } else {
              return (
                <div className="behavior-trend-empty">
                  <div>{t('behaviorChart.noTrendData')}</div>
                </div>
              );
            }
          })()}
        </div>
      </div>
    </div>
  );
});


const DiningBehaviorChart: React.FC = () => {
  const { t } = useTranslation();
  const { data: dashboardData, loading, error, salesforceId } = useDashboardData();
  const { 
    isFiltersApplying,
    dayOfWeek,
    time,
    diningBehavior 
  } = useFilters();

  
  const [selectedBehavior, setSelectedBehavior] = useState<string | null>(null);
  
  
  const [showMonthlyTrend, setShowMonthlyTrend] = useState<boolean>(false);
  const [modalPosition, setModalPosition] = useState<ModalPosition>({ y: 0 });

  
  const filters = useMemo(() => ({
    dayOfWeek: (!dayOfWeek.allDays && dayOfWeek.selectedDays.length > 0) ? dayOfWeek.selectedDays : undefined,
    hour: (!time.allHours && time.selectedHours.length > 0) ? time.selectedHours : undefined,
    behavior: undefined, 
    diningGroup: undefined,
    menuGroup: undefined
  }), [dayOfWeek, time, diningBehavior]);

  
  const handleBehaviorSelect = useCallback((behavior: string | null) => {
    setSelectedBehavior(behavior);
  }, []);

  const handleMonthlyTrendClick = useCallback((event: React.MouseEvent) => {
    const clickY = event.clientY;
    const modalHeight = 650;
    const padding = 20;
    const screenHeight = window.innerHeight;
    
    let finalY = clickY;
    if (clickY + modalHeight + padding > screenHeight) {
      finalY = screenHeight - modalHeight - padding;
    }
    if (finalY < padding) {
      finalY = padding;
    }
    
    setModalPosition({ y: finalY });
    setShowMonthlyTrend(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowMonthlyTrend(false);
  }, []);

  
  const isLaptopRender = window.innerWidth <= 1600;
  const isIPadRender = window.innerWidth <= 1024;
  const isIPhoneRender = window.innerWidth <= 480;

  if (loading || isFiltersApplying) {
    return (
      <div className="behavior-chart">
        <div className="behavior-title-band">
          <h2>{t('behaviorChart.title')}</h2>
        </div>
        <div className="behavior-chart-container chart-container">
          <div className="behavior-loading">
            <div className="behavior-loading-spinner"></div>
            <div className="behavior-loading-text">
              {isFiltersApplying ? t('behaviorChart.filterApplying') : t('behaviorChart.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="behavior-chart">
        <div className="behavior-title-band">
          <h2>{t('behaviorChart.title')}</h2>
        </div>
        <div className="behavior-chart-container">
          <div className="behavior-error">
            <div className="behavior-error-icon">⚠️</div>
            <div className="behavior-error-text">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="behavior-chart">
        {isFiltersApplying && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <span>{t('behaviorChart.filterApplying')}</span>
          </div>
        )}
        
        <div className="behavior-title-band">
          <h2 style={{
            fontSize: isIPhoneRender ? '14px' : (isIPadRender ? '15px' : (isLaptopRender ? '16px' : '18px')),
            padding: isIPhoneRender ? '6px 15px' : (isIPadRender ? '7px 18px' : (isLaptopRender ? '8px 20px' : '10px 25px'))
          }}>
            {t('behaviorChart.title')}
          </h2>
        </div>
        
        {}
        <MainBehaviorChart
          dashboardData={dashboardData}
          selectedBehavior={selectedBehavior}
          onBehaviorSelect={handleBehaviorSelect}
          onMonthlyTrendClick={handleMonthlyTrendClick}
          isFiltersApplying={isFiltersApplying}
          filters={filters}
        />
      </div>

      {}
      <MonthlyTrendModal
        show={showMonthlyTrend}
        onClose={handleCloseModal}
        salesforceId={salesforceId}
        modalPosition={modalPosition}
        
      />
    </>
  );
};

export default DiningBehaviorChart;